import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TCustomerGrowthData } from '../schemas';

type CustomerGrowthChartProps = {
  growthData: TCustomerGrowthData[];
  isLoading?: boolean;
  totalCustomers?: number;
};

const CustomerGrowthChart = ({ growthData, isLoading, totalCustomers }: CustomerGrowthChartProps) => {
  if (isLoading) {
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.loadingText}>Loading data...</Typography>
      </Box>
    );
  }

  if (!growthData || growthData.length === 0) {
    return (
      <Box sx={styles.container}>
        <Typography sx={styles.emptyText}>No growth data available</Typography>
      </Box>
    );
  }

  // Format data for Recharts
  const chartData = growthData.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    }),
    customers: item.count,
  }));

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box>
          <Typography sx={styles.title}>Customer Growth Analytics</Typography>
          <Typography sx={styles.subtitle}>Track customer acquisition over time</Typography>
        </Box>
        {totalCustomers !== undefined && (
          <Box sx={styles.statsCard}>
            <Typography sx={styles.statsLabel}>Total Customers</Typography>
            <Typography sx={styles.statsValue}>{totalCustomers.toLocaleString()}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorCustomers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#000000" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#000000" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fill: '#666', fontSize: 12, fontFamily: 'GilroyRegular' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#666', fontSize: 12, fontFamily: 'GilroyRegular' }}
              tickLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontFamily: 'GilroyRegular',
              }}
              labelStyle={{ fontFamily: 'GilroyBold', marginBottom: '4px' }}
            />
            <Area
              type="monotone"
              dataKey="customers"
              stroke="#000000"
              strokeWidth={2}
              fillOpacity={1}
              fill="url(#colorCustomers)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default CustomerGrowthChart;

const styles = {
  container: {
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    backgroundColor: '#FFF',
    padding: 3,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 3,
    flexWrap: 'wrap' as const,
    gap: 2,
  },
  title: {
    fontFamily: 'GilroyBold',
    fontSize: 20,
    color: '#1a1a1a',
    marginBottom: 0.5,
  },
  subtitle: {
    fontFamily: 'GilroyRegular',
    fontSize: 13,
    color: '#888',
  },
  statsCard: {
    backgroundColor: '#000',
    borderRadius: '12px',
    padding: '16px 24px',
    minWidth: '160px',
  },
  statsLabel: {
    fontFamily: 'GilroyBold',
    fontSize: 11,
    color: '#fff',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.8px',
    marginBottom: '6px',
  },
  statsValue: {
    fontFamily: 'GilroyBold',
    fontSize: 28,
    color: '#fff',
  },
  loadingText: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    textAlign: 'center' as const,
    padding: 4,
  },
  emptyText: {
    fontFamily: 'GilroyRegular',
    fontSize: 14,
    color: '#666',
    textAlign: 'center' as const,
    padding: 4,
  },
  chartWrapper: {
    width: '100%',
    height: 350,
  },
};
